import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shirt, Camera, ShoppingBag, ArrowRight, Menu, X, User, Scissors, HeadphonesIcon, ChevronRight, Shield, Heart, Gift } from 'lucide-react';

const NAVY = '#0B1426';
const GOLD = '#D4AF37';
const HERO_IMG = 'https://images.pexels.com/photos/12225348/pexels-photo-12225348.jpeg?auto=compress&cs=tinysrgb&w=900';

const STEPS = [
  { icon: Shirt, label: 'Select Design' },
  { icon: Camera, label: 'Click Pictures' },
  { icon: ShoppingBag, label: 'Place Order' },
];

const IntroScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isPrivileged = user && (user.role === 'admin' || user.role === 'founder');
  const menuItems = [
    { icon: Heart, label: 'Wishlist', path: '/wishlist', testId: 'menu-wishlist' },
    { icon: ShoppingBag, label: 'My Orders', path: '/my-orders', testId: 'menu-my-orders' },
    { icon: Gift, label: 'Refer & Earn', path: '/referrals', testId: 'menu-referrals' },
    { icon: User, label: user ? user.name || 'Account' : 'Sign In', path: user ? null : '/auth', testId: 'menu-account' },
    { icon: Scissors, label: 'Tailor Login', path: '/tailor-login', testId: 'menu-tailor-login' },
    ...(isPrivileged ? [{ icon: Shield, label: 'Admin', path: '/admin', testId: 'menu-admin' }] : []),
    { icon: HeadphonesIcon, label: 'Help Center', path: '/info/help', testId: 'menu-support' },
  ];

  return (
    <div data-testid="intro-screen" className="min-h-screen flex flex-col"
      style={{ backgroundColor: NAVY, fontFamily: 'Outfit, sans-serif', color: '#F5F1E6' }}>
      <style>{`@keyframes lnFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .ln-rise { opacity: 0; animation: lnFadeUp .7s ease-out forwards; }`}</style>

      <header className="flex items-center justify-between px-6 pt-5 pb-3">
        <div data-testid="landing-brand">
          <p className="text-xl tracking-[0.22em]" style={{ fontFamily: 'Playfair Display, serif', color: GOLD }}>LUNORA’S</p>
          <p className="text-[10px] tracking-[0.35em] uppercase mt-0.5" style={{ color: 'rgba(245,241,230,0.6)' }}>Wear the Vibe</p>
        </div>
        <button data-testid="header-menu-btn" aria-label="Open menu" onClick={() => setMenuOpen(true)}
          className="p-2 -mr-2" style={{ color: GOLD }}>
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </header>

      {/* Slide-in Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="menu-overlay">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setMenuOpen(false)}
            style={{ backdropFilter: 'blur(4px)' }}
          />
          <div 
            className="absolute right-0 top-0 h-full w-[280px] sm:w-[320px] flex flex-col shadow-2xl"
            style={{ 
              backgroundColor: '#FAF9F6',
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: '#E5E5E5' }}>
              <span 
                className="text-lg"
                style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}
              >
                Menu
              </span>
              <button
                data-testid="menu-close-btn"
                onClick={() => setMenuOpen(false)}
                className="p-1"
              >
                <X className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 py-4">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  data-testid={item.testId}
                  onClick={() => {
                    setMenuOpen(false);
                    if (item.path) navigate(item.path);
                  }}
                  className="w-full flex items-center gap-4 px-6 py-4 transition-colors hover:bg-black/[0.03]"
                >
                  <item.icon className="w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                  <span className="text-sm flex-1 text-left" style={{ color: '#0F0F0F', letterSpacing: '0.02em' }}>
                    {item.label}
                  </span>
                  <ChevronRight className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                </button>
              ))}
            </div>

            {/* Menu Footer */}
            <div className="px-6 py-5 border-t space-y-2" style={{ borderColor: '#E5E5E5' }}>
              {user && (
                <button
                  data-testid="menu-logout-btn"
                  onClick={async () => { setMenuOpen(false); await logout(); }}
                  className="w-full py-3 text-xs tracking-[0.1em] uppercase text-center border"
                  style={{ borderColor: '#E5E5E5', color: '#991B1B' }}
                >
                  Sign Out
                </button>
              )}
              {!user && (
                <button
                  data-testid="menu-signin-btn"
                  onClick={() => { setMenuOpen(false); navigate('/auth'); }}
                  className="w-full py-3 text-xs tracking-[0.1em] uppercase text-center text-white"
                  style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
                >
                  Sign In / Create Account
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col px-6 pb-8 max-w-md w-full mx-auto">
        <div className="ln-rise relative mt-3 overflow-hidden" style={{ animationDelay: '0.05s', border: `1px solid ${GOLD}40` }}>
          <img data-testid="landing-hero-image" src={HERO_IMG} alt="Lunora's premium ethnic wear"
            className="w-full h-[44vh] min-h-[280px] object-cover object-top" />
          <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: `linear-gradient(to top, ${NAVY}, transparent)` }} />
        </div>

        <h1 data-testid="landing-headline" className="ln-rise text-4xl leading-tight mt-7"
          style={{ fontFamily: 'Playfair Display, serif', animationDelay: '0.2s', letterSpacing: '-0.01em' }}>
          Your Perfect Fit, <span style={{ color: GOLD }}>Your Style</span>
        </h1>
        <p data-testid="landing-subtitle" className="ln-rise text-sm leading-relaxed mt-3"
          style={{ color: 'rgba(245,241,230,0.7)', animationDelay: '0.3s' }}>
          Choose your design. Get your measurements. Place your order.
        </p>

        <ol data-testid="landing-steps" className="ln-rise flex items-start justify-between mt-8" style={{ animationDelay: '0.4s' }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.label}>
              <li data-testid={`landing-step-${i + 1}`} className="flex flex-col items-center text-center w-20">
                <span className="w-11 h-11 rounded-full flex items-center justify-center"
                  style={{ border: `1px solid ${GOLD}`, color: GOLD }}>
                  <s.icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <span className="text-[11px] mt-2 leading-tight" style={{ color: 'rgba(245,241,230,0.85)' }}>
                  {i + 1}. {s.label}
                </span>
              </li>
              {i < STEPS.length - 1 && <ArrowRight className="w-4 h-4 mt-3.5 shrink-0" style={{ color: `${GOLD}99` }} />}
            </React.Fragment>
          ))}
        </ol>

        <button data-testid="get-started-btn" onClick={() => navigate('/landing')}
          className="ln-rise w-full rounded-full text-sm tracking-[0.18em] uppercase flex items-center justify-center gap-2 transition-transform duration-200 active:scale-[0.98] hover:-translate-y-0.5"
          style={{ height: 36, padding: '0 1rem', lineHeight: 1, backgroundColor: GOLD, color: NAVY, fontWeight: 600, animationDelay: '0.55s', marginTop: '2.5rem' }}>
          Get Started <ArrowRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </main>
    </div>
  );
};

export default IntroScreen;
